import type { CustomerOrder } from '../../customerOrdersApi'
import type { CustomerProfile } from '../../types'

export type OrderTimelineStepId = 'created' | 'paid' | 'shipped'

export const LocationIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} stroke="currentColor" strokeWidth={1.8} aria-hidden>
    <path d="M12 21s6-5.2 6-10a6 6 0 1 0-12 0c0 4.8 6 10 6 10Z" />
    <circle cx="12" cy="11" r="2.2" />
  </svg>
)

export const isSafeExternalUrl = (value: string | null): value is string => {
  if (!value) return false

  try {
    const parsed = new URL(value)
    return parsed.protocol === 'https:' || parsed.protocol === 'http:'
  } catch {
    return false
  }
}

export const toStepState = (
  order: CustomerOrder,
  step: OrderTimelineStepId,
): 'completed' | 'current' | 'upcoming' => {
  if (order.status === 'cancelled') {
    if (step === 'created') return 'completed'
    if (step === 'paid') return order.paidAt ? 'completed' : 'upcoming'
    return 'upcoming'
  }

  if (order.status === 'shipped') return 'completed'

  if (order.status === 'paid') {
    if (step === 'created') return 'completed'
    if (step === 'paid') return 'current'
    return 'upcoming'
  }

  if (step === 'created') return 'current'
  return 'upcoming'
}

export const resolveTimelineProgressPercent = (order: CustomerOrder) => {
  if (order.status === 'shipped') return 100
  if (order.status === 'paid') return 56
  if (order.status === 'cancelled') return order.paidAt ? 56 : 22
  return 22
}

export const resolveShippingLines = (order: CustomerOrder, profile: CustomerProfile) => {
  const contact = order.shippingContact ?? order.billingContact
  if (contact) {
    return {
      lines: [
        `${contact.firstName} ${contact.lastName}`.trim(),
        contact.company,
        contact.addressLine1,
        contact.addressLine2,
        `${contact.postalCode} ${contact.city}`.trim(),
        contact.province,
        contact.country,
      ].filter((line): line is string => Boolean(line)),
      source: 'order_snapshot' as const,
    }
  }

  const profileFallback =
    profile.shippingAddress.trim() || profile.billingAddress.trim() || profile.residentialAddress.trim()

  if (!profileFallback) {
    return {
      lines: [],
      source: 'unavailable' as const,
    }
  }

  return {
    lines: profileFallback.split(/\n|,/g).map((line) => line.trim()).filter(Boolean),
    source: 'profile_fallback' as const,
  }
}
